// ViewInvoicingData.js
import Box from "@mui/material/Box";
import { DataGrid } from "@mui/x-data-grid";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { Grid, FormControl, TextField } from "@mui/material";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import { useState, useEffect } from "react";
import { CSVLink } from "react-csv";
import axios from "../../../../api/axios";

const URL = "./dealership/getRebateDealershipTotalCost";
const REBATEURL = "./dealership/getDealershipRebatePerc";

function ViewInvoicingData() {
  /* -------------------- STATE -------------------- */
  const [warrantyList, setWarrantyList] = useState([]);
  const [rebateList, setRebateList] = useState([]);
  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);

  const [dealership, setDealership] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [alertOpen, setAlertopen] = useState(false);
  const [severity, setSeverity] = useState("");
  const [message, setMessage] = useState("");

  /* -------------------- COLUMNS -------------------- */
  const columns = [
    { field: "slNo", headerName: "ID", width: 60 },
    {
      field: "rebate_range",
      headerName: "Rebate Range",
      width: 230,
      valueFormatter: (params) => params.value || "-",
    },
    { field: "tradeName", headerName: "Dealership", width: 180 },
    {
      field: "totalCost",
      headerName: "Total Billable Cost",
      width: 160,
      valueFormatter: (params) =>
        `$${Number(params.value || 0).toLocaleString(undefined, {
          minimumFractionDigits: 2,
        })}`,
    },
    {
      field: "rebate_percent",
      headerName: "Rebate %",
      width: 120,
      valueFormatter: (params) => `${params.value || 0}%`,
    },
    {
      field: "rebateAmount",
      headerName: "Rebate Amount",
      width: 160,
      valueFormatter: (params) =>
        `$${Number(params.value || 0).toLocaleString(undefined, {
          minimumFractionDigits: 2,
        })}`,
    },
  ];

  /* -------------------- EFFECTS -------------------- */
  useEffect(() => {
    loadWarrantyData();
  }, []);

  useEffect(() => {
    if (warrantyList.length) loadRebateData();
  }, [warrantyList]);

  useEffect(() => {
    if (warrantyList.length && rebateList.length) {
      const result = buildRebateGridRows(warrantyList, rebateList);
      setRows(result);
      setFilteredRows(result);
    }
  }, [warrantyList, rebateList]);

  useEffect(() => {
    let temp = rows;

    if (dealership) {
      temp = temp.filter((r) =>
        r.tradeName?.toLowerCase().includes(dealership.toLowerCase())
      );
    }

    setFilteredRows(temp);
  }, [dealership, rows]);

  /* -------------------- API CALLS -------------------- */
  const loadWarrantyData = async () => {
    try {
      const res = await axios.post(URL);
      const formatted = (res.data.data || []).map((item, i) => ({
        id: i + 1,
        ...item,
        currentDate: formatDate(item.currentDate),
      }));
      setWarrantyList(formatted);
    } catch {
      setWarrantyList([]);
    }
  };

  const loadRebateData = async () => {
    try {
      const res = await axios.post(REBATEURL);
      const formatted = (res.data.data || []).map((r) => ({
        ...r,
        startDate: formatDate(r.startDate),
        endDate: formatDate(r.endDate) || getToday(),
      }));
      setRebateList(formatted);
    } catch {
      setRebateList([]);
    }
  };

  /* -------------------- CORE LOGIC -------------------- */
  function buildRebateGridRows(warrantyData, rebateData) {
    const groupMap = {};
    let slNo = 1;

    warrantyData.forEach((w) => {
      if (!w.currentDate) return;

      const closedDate = new Date(w.currentDate);
      const dealershipId = w.dealership;
      const cost = Number(String(w.totalCost || 0).replace(/,/g, ""));

      // 🔍 Find matching rebate (date + dealership)
      const rebate = rebateData.find((r) => {
        if (String(r.dealership_id) !== String(dealershipId)) return false;

        const start = new Date(r.startDate);
        const end = new Date(r.endDate);

        return closedDate >= start && closedDate <= end;
      });

      // ❌ NO REBATE → DO NOT PRINT
      if (!rebate) return;

      const rebateRange = `${rebate.startDate} to ${rebate.endDate}`;
      const rebatePercent = rebate.rebate_percent;

      const key = `${dealershipId}|${rebateRange}|${rebatePercent}`;

      if (!groupMap[key]) {
        groupMap[key] = {
          id: slNo,
          slNo: slNo++,
          rebate_range: rebateRange,
          tradeName: w.tradeName,
          totalCost: cost,
          rebate_percent: rebatePercent,
          rebateAmount: 0,
        };
      } else {
        groupMap[key].totalCost += cost;
      }
    });

    // 🔢 Final calculation
    return Object.values(groupMap).map((row, index) => ({
      ...row,
      id: index + 1, // ✅ safe unique id for MUI
      totalCost: row.totalCost.toFixed(2),
      rebateAmount: (
        (row.totalCost * row.rebate_percent) /
        100
      ).toFixed(2),
    }));
  }



  /* -------------------- HELPERS -------------------- */
  const formatDate = (d) => {
    if (!d || d === "0000-00-00") return null;
    const date = new Date(d);
    if (isNaN(date)) return null;
    return `${date.getFullYear()}-${String(
      date.getMonth() + 1
    ).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
  };

  const getToday = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
      2,
      "0"
    )}-${String(d.getDate()).padStart(2, "0")}`;
  };

  /* -------------------- UI -------------------- */
  return (
    <div style={{ marginTop: 100 }}>
      <Box sx={{ p: 2 }}>
        <Grid container spacing={2} mb={2}>
          <Grid item xs={6}>
            <FormControl fullWidth>
              <Typography variant="subtitle2">Dealership</Typography>
              <TextField
                value={dealership}
                onChange={(e) => setDealership(e.target.value)}
              />
            </FormControl>
          </Grid>
        </Grid>

        <DataGrid
          rows={filteredRows}
          columns={columns}
          pageSizeOptions={[10, 25, 50]}
          initialState={{
            pagination: { paginationModel: { pageSize: 25, page: 0 } },
          }}
          autoHeight
        />

        <Box mt={2}>
          <CSVLink
            data={filteredRows}
            filename="rebate-summary.csv"
            style={{
              background: "#0d2365",
              color: "#fff",
              padding: "8px 16px",
              borderRadius: 6,
              textDecoration: "none",
            }}
          >
            Export CSV
          </CSVLink>
        </Box>
      </Box>

      <Snackbar open={alertOpen} autoHideDuration={6000}>
        <Alert severity={severity} variant="filled">
          {message}
        </Alert>
      </Snackbar>
    </div>
  );
}

export default ViewInvoicingData;
